import { useEffect, useRef } from "react";
import "./styling/aboutMe.css";

function AboutMe({ revealRequested = false }) {
  const sectionRef = useRef(null);
  const revealed = revealRequested;

  useEffect(() => {
    if (!revealed) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      sectionRef.current.scrollIntoView?.({
        behavior: "auto",
        block: "nearest",
      });
    }
  }, [revealed]);

  function finishExpansion(event) {
    if (
      revealed &&
      event.target === event.currentTarget &&
      event.propertyName === "grid-template-rows"
    ) {
      sectionRef.current.scrollIntoView?.({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }

  return (
    <section
      ref={sectionRef}
      className="about-section"
      id="about"
      aria-label="About Lindsay Zhang"
      tabIndex="-1"
      data-expanded={revealed}
      onTransitionEnd={finishExpansion}
    >
      <div className="about-space">
        <div
          className="about-card"
          data-revealed={revealed}
          style={{ visibility: revealed ? "visible" : "hidden" }}
        >
          <div className="about-card-front">
            <div className="about-intro">
              <h1 id="intro-title">hi, i'm linds!</h1>
              <div className="about-photo">
                <span className="sr-only">
                  Lindsay Zhang · Waterloo Software Engineering
                </span>
                <svg
                  className="profile-caption"
                  viewBox="0 0 224 224"
                  aria-hidden="true"
                  focusable="false"
                >
                  <defs>
                    <path
                      id="profile-name-arc"
                      d="M20 112 A92 92 0 0 1 204 112"
                    />
                    <path
                      id="profile-school-arc"
                      d="M8 112 A104 104 0 0 0 216 112"
                    />
                  </defs>
                  <text>
                    <textPath
                      href="#profile-name-arc"
                      startOffset="50%"
                      textAnchor="middle"
                    >
                      Lindsay Zhang
                    </textPath>
                  </text>
                  <text>
                    <textPath
                      href="#profile-school-arc"
                      startOffset="50%"
                      textAnchor="middle"
                    >
                      Waterloo Software Engineering
                    </textPath>
                  </text>
                </svg>
                <img
                  className="profile-pic"
                  src="/images/lindsay-zhang-outdoors.webp"
                  alt="Lindsay Zhang"
                  width="640"
                  height="480"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
            <div className="about-copy">
              <p>
                I'm in my last year of Software Engineering at the University of
                Waterloo. I spent 8 months at{" "}
                <a
                  href="https://www.mintlify.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Mintlify<span className="sr-only"> (opens in a new tab)</span>
                </a>
                , where I built Mintlify Index, AI Editor infrastructure, and
                revamped the production queueing system. Before that, I was at X
                <sup className="footnote-reference">
                  <a
                    id="xchats-reference"
                    href="#xchats-note"
                    role="doc-noteref"
                    aria-label="Read footnote 1 about XChats"
                  >
                    1
                  </a>
                </sup>{" "}
                and theScore.
              </p>
              <p>
                I've worked a lot and studied a lot, so now I'm currently
                focusing on building things for fun! I'm also spending a lot of
                time organizing a women+
                <sup className="footnote-reference">
                  <a
                    id="women-in-tech-reference"
                    href="#women-in-tech-note"
                    role="doc-noteref"
                    aria-label="Read footnote 2 about being a woman in tech"
                  >
                    2
                  </a>
                </sup>{" "}
                hackathon,{" "}
                <a
                  href="https://itstechnova.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  TechNova<span className="sr-only"> (opens in a new tab)</span>
                </a>
                , finding ways to enjoy my last year in school, and hopefully
                fishing
                <sup className="footnote-reference">
                  <a
                    id="fishing-reference"
                    href="#fishing-note"
                    role="doc-noteref"
                    aria-label="Read footnote 3 about what I've caught"
                  >
                    3
                  </a>
                </sup>
                .
              </p>
              <p
                className="small footnote"
                id="xchats-note"
                role="doc-footnote"
                tabIndex="-1"
              >
                <sup>
                  <a
                    href="#xchats-reference"
                    role="doc-backlink"
                    aria-label="Back to reference 1"
                  >
                    1
                  </a>
                </sup>{" "}
                Pssst if you use XChats on Twitter, you're using smth I helped
                build!!
              </p>
              <p
                className="small footnote"
                id="women-in-tech-note"
                role="doc-footnote"
                tabIndex="-1"
              >
                <sup>
                  <a
                    href="#women-in-tech-reference"
                    role="doc-backlink"
                    aria-label="Back to reference 2"
                  >
                    2
                  </a>
                </sup>{" "}
                <a
                  className="footnote-external-link"
                  href="https://linz591304.substack.com/p/the-modern-day-struggle-of-being?r=5cgkq7&utm_campaign=post-expanded-share&utm_medium=post%20viewer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  how i feel about being a woman in tech
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path d="M15 3h6v6M10 14 21 3" />
                    <path d="M21 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6" />
                  </svg>
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              </p>
              <div
                className="small footnote fishing-footnote"
                id="fishing-note"
                role="doc-footnote"
                tabIndex="-1"
              >
                <sup>
                  <a
                    href="#fishing-reference"
                    role="doc-backlink"
                    aria-label="Back to reference 3"
                  >
                    3
                  </a>
                </sup>
                <div className="fishing-note-copy">
                  <details className="fishing-list">
                    <summary>
                      <span className="fishing-toggle-label">
                        i'm not great at fishing but i'm no larper (hopefully)
                      </span>
                      <span className="fishing-toggle-icon" aria-hidden="true" />
                    </summary>
                    <div className="fishing-content">
                      <span>here's some things ive caught so far:</span>
                      <ul className="fishing-catches">
                        <li>jacksmelt</li>
                        <li>flounder</li>
                        <li>skate</li>
                        <li>smooth dogfish</li>
                      </ul>
                    </div>
                  </details>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AboutMe;
